import Settings from '../domain/Settings';
import {hasPurposesConsent, hasVendorsConsent} from '../services/privacyService';
import * as storage from '../utils/storage';
import * as performance from '../utils/performance';
import logger from '../utils/logger';
import {getDMOI} from '../services/userService';
import {loadResource, ResourceType} from '../utils/resource-util';

export type Config = {
	config?: {
		onProfileReady: (profile: Profile) => void
	}
	cmd?: AnyFunction[]
};

export type API = Config & {
	setUserConsent: (consent: { lotameConsent: boolean }) => void
	sync: () => void
};

export type Profile = {
	getProfileId: () => string
}

type CacheValue = {
	lotameId: string
	expiration: string
	pending?: boolean
};

declare global {
	interface Window {
		[key: `lotame_sync_${string}`]: Config | API;
	}
}

const STORAGE_KEY = 'advert-lotame',
	LOTAME_ID_MAPPER_API_URL = 'https://api-dpg-advertising-lotame-id.dpgmedia.net/ingest';

export function init(): void {
	const settings = Settings.getInstance();

	if (!settings.features?.lotame)
		return;

	if (!settings.lotame?.clientId) {
		logger.error('Tried loading Lotame without client id, skipping.');
		return;
	}

	if (!hasConsent()) {
		return;
	}

	performance.mark('lotame - start');

	try {
		const cachedValue = getCachedValue();

		if (cachedValue) {
			// ID was already fetched, but couldn't combine it with a DMOI, so retry
			if (cachedValue.pending) {
				trySendToAPI(cachedValue);
			}
			return;
		}

		configureLotame();
		performance.mark('lotame - configured');
	} finally {
		performance.mark('lotame - end');
	}
}

function configureLotame() {
	const clientId = Settings.getInstance().lotame!.clientId,
		syncTag: `lotame_sync_${string}` = `lotame_sync_${clientId}`;

	window[syncTag] = {
		config: {
			onProfileReady
		},
		cmd: []
	};

	// Register consent, checked in init
	window[syncTag].cmd.push(() => {
		const api = window[syncTag] as API;

		api.setUserConsent({lotameConsent: true});
		api.sync();
	});

	// Load the script
	loadResource({
		type: ResourceType.Script,
		content: `https://tags.crwdcntrl.net/lt/c/${clientId}/sync.min.js`
	});
}

function onProfileReady(profile: Profile) {
	const lotameId = profile.getProfileId();

	trySendToAPI({lotameId});
}

function getCachedValue(): CacheValue | null {
	const storedValue = JSON.parse(storage.get(STORAGE_KEY) ?? null) as CacheValue | null;

	if (storedValue && new Date(storedValue.expiration) > new Date()) {
		return storedValue;
	}

	return null;
}

function addToCache(cacheValue: CacheValue | Omit<CacheValue, 'expiration'>) {
	// No need to check purpose 1 as it's already covered in init
	storage.put(STORAGE_KEY, JSON.stringify({
		expiration: getExpirationDate(),
		...cacheValue,
	}));
}

function hasConsent(): boolean {
	return hasPurposesConsent([1, 4]) && hasVendorsConsent([95]);
}

function getExpirationDate(): string {
	const currDate = new Date();

	currDate.setDate(currDate.getDate() + 1);

	return currDate.toISOString();
}

function trySendToAPI(cachedValue: CacheValue | Omit<CacheValue, 'expiration'>) {
	const dmoi = getDMOI();

	if (dmoi) {
		const body = JSON.stringify({
				lotameId: cachedValue.lotameId,
				dmoi
			}),
			content = new Blob([body], {type: 'charset=utf-8'});

		navigator.sendBeacon(LOTAME_ID_MAPPER_API_URL, content);

		addToCache({
			...cachedValue,
			pending: undefined,
		});
		performance.mark('lotame - id map sent');
	} else {
		addToCache({
			...cachedValue,
			pending: true,
		});
		performance.mark('lotame - id map pending');
	}
}
